import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { FileText, Download, Loader2, CheckCircle, AlertCircle, Building2, Construction } from 'lucide-react';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';

const GenerateAttestationRCDS = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [contractType, setContractType] = useState('BATICO');
  
  const [formData, setFormData] = useState({
    attestation_id: `RCDS_2025_${Date.now().toString().slice(-6)}`,
    contract_type: 'BATICO',
    raison_sociale: '',
    forme_juridique: 'SARL',
    adresse_siege: '',
    siret: '',
    representant_legal: '',
    date_debut: '01/01/2025',
    date_fin: '31/12/2025',
    // Champs pour conventions NOMINATIFS
    reference_marche: '',
    intitule_projet: '',
    maitre_ouvrage: '',
    montant_marche: '',
    duree_chantier: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleContractTypeChange = (type) => {
    setContractType(type);
    setFormData({
      ...formData,
      contract_type: type
    });
  };

  const isNominative = contractType === 'NOMINATIVE_BATICO' || contractType === 'NOMINATIVE_ARCHOS';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate/attestation-rcds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de la génération');
      }

      // Télécharger le PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_rcds_${formData.attestation_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Bouton retour */}
      <BackButton label="Retour" className="mb-4" />
      
      <Card className="border-l-4 border-l-coppam-blue">
        <CardHeader className="bg-gradient-to-r from-coppam-blue to-blue-600 text-white">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <FileText className="w-6 h-6" />
            Génération Attestation RCDS
          </CardTitle>
          <CardDescription className="text-white/90 text-base">
            Générez une attestation RCDS avec signature horodatée, tampon et QR Code
          </CardDescription>
        </CardHeader>
        
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sélection du Type de Convention */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-coppam-blue border-b-2 border-coppam-gold pb-2">
                Type de Convention RCDS
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleContractTypeChange('BATICO')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    contractType === 'BATICO'
                      ? 'border-coppam-gold bg-coppam-light-blue text-coppam-blue'
                      : 'border-gray-300 hover:border-coppam-blue'
                  }`}
                >
                  <Building2 className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">BATICO</div>
                  <div className="text-xs">Annuel - Bâtiment</div>
                </button>

                <button
                  type="button"
                  onClick={() => handleContractTypeChange('ARCHOS')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    contractType === 'ARCHOS'
                      ? 'border-coppam-gold bg-coppam-light-blue text-coppam-blue'
                      : 'border-gray-300 hover:border-coppam-blue'
                  }`}
                >
                  <Construction className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">ARCHOS</div>
                  <div className="text-xs">Annuel - Travaux Publics</div>
                </button>

                <button
                  type="button"
                  onClick={() => handleContractTypeChange('NOMINATIVE_BATICO')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    contractType === 'NOMINATIVE_BATICO'
                      ? 'border-coppam-gold bg-coppam-light-blue text-coppam-blue'
                      : 'border-gray-300 hover:border-coppam-blue'
                  }`}
                >
                  <Building2 className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">NOMINATIVE BATICO</div>
                  <div className="text-xs">Par Chantier - Bâtiment</div>
                </button>

                <button
                  type="button"
                  onClick={() => handleContractTypeChange('NOMINATIVE_ARCHOS')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    contractType === 'NOMINATIVE_ARCHOS'
                      ? 'border-coppam-gold bg-coppam-light-blue text-coppam-blue'
                      : 'border-gray-300 hover:border-coppam-blue'
                  }`}
                >
                  <Construction className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">NOMINATIVE ARCHOS</div>
                  <div className="text-xs">Par Chantier - TP</div>
                </button>
              </div>
            </div>

            {/* Identification */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-coppam-blue border-b-2 border-coppam-gold pb-2">
                Identification du Membre
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="attestation_id">N° Attestation *</Label>
                  <Input
                    id="attestation_id"
                    name="attestation_id"
                    value={formData.attestation_id}
                    onChange={handleChange}
                    required
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="forme_juridique">Forme Juridique *</Label>
                  <Input
                    id="forme_juridique"
                    name="forme_juridique"
                    value={formData.forme_juridique}
                    onChange={handleChange}
                    required
                    placeholder="SARL, SAS, EURL..."
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="raison_sociale">Raison Sociale *</Label>
                <Input
                  id="raison_sociale"
                  name="raison_sociale"
                  value={formData.raison_sociale}
                  onChange={handleChange}
                  required
                  placeholder="ENTREPRISE BTP GUADELOUPE SARL"
                  className="border-coppam-blue/30 focus:border-coppam-gold"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="adresse_siege">Adresse du Siège Social *</Label>
                <Input
                  id="adresse_siege"
                  name="adresse_siege"
                  value={formData.adresse_siege}
                  onChange={handleChange}
                  required
                  placeholder="Rue, Code Postal, Ville, Territoire"
                  className="border-coppam-blue/30 focus:border-coppam-gold"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="siret">N° SIRET *</Label>
                  <Input
                    id="siret"
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                    required
                    placeholder="123 456 789 00012"
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="representant_legal">Représentant Légal *</Label>
                  <Input
                    id="representant_legal"
                    name="representant_legal"
                    value={formData.representant_legal}
                    onChange={handleChange}
                    required
                    placeholder="M. Jean DUPONT - Gérant"
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>
              </div>
            </div>

            {/* Période de validité */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-coppam-blue border-b-2 border-coppam-gold pb-2">
                {isNominative ? 'Période du Chantier' : 'Période de Validité'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_debut">Date de Début *</Label>
                  <Input
                    id="date_debut"
                    name="date_debut"
                    value={formData.date_debut}
                    onChange={handleChange}
                    required
                    placeholder="01/01/2025"
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date_fin">Date de Fin *</Label>
                  <Input
                    id="date_fin"
                    name="date_fin"
                    value={formData.date_fin}
                    onChange={handleChange}
                    required
                    placeholder="31/12/2025"
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>
              </div>
            </div>

            {/* Informations Marché (pour conventions NOMINATIFS uniquement) */}
            {isNominative && (
              <div className="space-y-4 bg-yellow-50 border-2 border-coppam-gold/50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-coppam-blue border-b-2 border-coppam-gold pb-2">
                  Informations Marché / Chantier Spécifique
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="reference_marche">Référence Marché *</Label>
                  <Input
                    id="reference_marche"
                    name="reference_marche"
                    value={formData.reference_marche}
                    onChange={handleChange}
                    required={isNominative}
                    placeholder="MARCHE-2025-001"
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="intitule_projet">Intitulé du Projet *</Label>
                  <Input
                    id="intitule_projet"
                    name="intitule_projet"
                    value={formData.intitule_projet}
                    onChange={handleChange}
                    required={isNominative}
                    placeholder="Construction d'un bâtiment administratif"
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maitre_ouvrage">Maître d'Ouvrage *</Label>
                  <Input
                    id="maitre_ouvrage"
                    name="maitre_ouvrage"
                    value={formData.maitre_ouvrage}
                    onChange={handleChange}
                    required={isNominative}
                    placeholder="Collectivité Territoriale de Guadeloupe"
                    className="border-coppam-blue/30 focus:border-coppam-gold"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="montant_marche">Montant du Marché (€ Montant de base) *</Label>
                    <Input
                      id="montant_marche"
                      name="montant_marche"
                      value={formData.montant_marche}
                      onChange={handleChange}
                      required={isNominative}
                      placeholder="2 500 000"
                      className="border-coppam-blue/30 focus:border-coppam-gold"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duree_chantier">Durée du Chantier *</Label>
                    <Input
                      id="duree_chantier"
                      name="duree_chantier"
                      value={formData.duree_chantier}
                      onChange={handleChange}
                      required={isNominative}
                      placeholder="18 mois"
                      className="border-coppam-blue/30 focus:border-coppam-gold"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="border-green-500 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  ✅ Attestation RCDS générée avec succès ! Le téléchargement a commencé.
                </AlertDescription>
              </Alert>
            )}

            {/* Bouton de soumission */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-coppam-gold hover:bg-coppam-gold/90 text-white font-semibold py-6 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  Générer l'Attestation PDF
                </>
              )}
            </Button>

            <p className="text-sm text-gray-500 text-center">
              L'attestation sera générée avec signature horodatée de F.PIPEROL (Président), 
              tampon COPPAM et QR Code de vérification.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default GenerateAttestationRCDS;
